import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';

// Helper functions for safe image URL extraction
export const getSafeImageUrl = (
  thumbnail: { width: number; height: number; url: string } | undefined | null,
  fallbackUrl?: string
): string => {
  return thumbnail?.url || fallbackUrl || '';
};

export const getSafeThumbnailUrl = (
  design: { thumbnail?: { width: number; height: number; url: string } | null } | undefined,
  fallbackUrl?: string
): string => {
  return design?.thumbnail?.url || fallbackUrl || '';
};

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  fallbackText?: string;
  style?: React.CSSProperties;
  className?: string;
  onError?: () => void;
  onLoad?: () => void;
}

export const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt,
  fallbackText = 'Image not available',
  style = {},
  className,
  onError,
  onLoad,
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoading(false);
    onError?.();
  }, [onError]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    onLoad?.();
  }, [onLoad]);

  if (hasError || !src) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          ...style,
        }}
        className={className}
      >
        <Typography variant="caption" color="textSecondary">
          {fallbackText}
        </Typography>
      </Box>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={{
        opacity: isLoading ? 0.5 : 1,
        transition: 'opacity 0.2s',
        ...style,
      }}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};